import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded'
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import { getHelperSnapshot, subscribeHelperStore, type HelperStoreSnapshot } from '../../services/helper/helperStore'
import { refreshHelperQueue } from '../../services/helper/queueService'
import { drainOutbox } from '../../services/helper/syncWorker'
import { buildMobileSyncSummary, type MobileSyncTone } from '../../services/mobileSync/mobileSyncSummary'
import { usePushBackHandler } from '../../context/BackStackContext'
import { Button, Pressable } from '../Common'
import { useNls } from '../../nls/nlsProvider'

type Props = {
  onOpenSyncCenter: () => void
  className?: string
}

const TONE_ICON: Record<MobileSyncTone, ReactNode> = {
  healthy: <CloudDoneRoundedIcon fontSize="small" />,
  working: <SyncRoundedIcon fontSize="small" />,
  offline: <CloudOffRoundedIcon fontSize="small" />,
  attention: <ErrorOutlineRoundedIcon fontSize="small" />,
}

const useHelperSyncSnapshot = () => {
  const [snapshot, setSnapshot] = useState<HelperStoreSnapshot>(() => getHelperSnapshot())
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine)

  useEffect(() => {
    const unsubscribe = subscribeHelperStore(setSnapshot)
    void refreshHelperQueue()
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      unsubscribe()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { snapshot, online }
}

export const HelperSyncPill = ({ onOpenSyncCenter, className = '' }: Props) => {
  const { t } = useNls()
  const { snapshot, online } = useHelperSyncSnapshot()
  const [open, setOpen] = useState(false)
  const [syncingLocal, setSyncingLocal] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const syncNowRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressTap = useRef(false)
  const summary = useMemo(() => buildMobileSyncSummary({
    syncState: snapshot.syncState,
    pendingCount: snapshot.pending.length,
    failedCount: snapshot.failed.length,
    completedCount: snapshot.recentCompleted.length,
    lastSyncAt: snapshot.lastSyncAt,
    isOnline: online,
  }), [online, snapshot.failed.length, snapshot.lastSyncAt, snapshot.pending.length, snapshot.recentCompleted.length, snapshot.syncState])
  const count = snapshot.failed.length || snapshot.pending.length
  const showLabel = count > 0 || summary.headline !== 'Ready to work'
  const isActiveTone = summary.tone !== 'healthy'

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    requestAnimationFrame(() => {
      suppressTap.current = false
    })
  }

  useEffect(() => () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
  }, [])

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true
      syncNowRef.current?.focus()
      return
    }
    if (wasOpenRef.current) {
      triggerRef.current?.focus()
      wasOpenRef.current = false
    }
  }, [open])

  usePushBackHandler(() => setOpen(false), open)

  return (
    <div className="relative shrink-0">
      <Pressable
        ref={triggerRef}
        className={`mobileSyncPill mobileSyncPill--${summary.tone} ${className}`}
        onClick={() => {
          if (suppressTap.current) return
          if (isActiveTone) setOpen((current) => !current)
          else onOpenSyncCenter()
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return
          longPressTimer.current = setTimeout(() => {
            longPressTimer.current = null
            suppressTap.current = true
            setOpen(true)
          }, 500)
        }}
        onPointerUp={clearLongPress}
        onPointerCancel={clearLongPress}
        onPointerLeave={clearLongPress}
        aria-label={t('NLSHelperSyncPillOpenAria')}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="helper-sync-pill"
      >
        {TONE_ICON[summary.tone]}
        {showLabel ? <span>{count > 0 ? t('NLSHelperSyncPillPending', { count }) : summary.headline}</span> : null}
      </Pressable>

      <AnimatePresence>
        {open ? (
          <>
            <Pressable
              className="fixed inset-0 z-30 bg-[color-mix(in_srgb,var(--bg)_60%,transparent)]"
              onClick={() => setOpen(false)}
              aria-label={t('NLSHelperSyncPillCloseAria')}
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="absolute top-full mt-2 right-0 z-40 w-[240px] bg-[var(--surface-card)] border border-[var(--border-subtle)] rounded-2xl shadow-[var(--shadow-pop)] p-3 flex flex-col gap-2"
              role="menu"
              tabIndex={-1}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault()
                  setOpen(false)
                }
              }}
              data-testid="helper-sync-pill-popover"
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 text-[var(--text-strong)]" aria-hidden="true">{TONE_ICON[summary.tone]}</span>
                <div className="min-w-0">
                  <span className="text-sm text-[var(--text-strong)]">{summary.headline}</span>
                  {summary.detail ? <span className="block text-[11px] text-[var(--muted)]">{summary.detail}</span> : null}
                </div>
              </div>
              <Button
                ref={syncNowRef}
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => {
                  setSyncingLocal(true)
                  void drainOutbox().finally(() => {
                    setSyncingLocal(false)
                    setOpen(false)
                  })
                }}
                disabled={syncingLocal || summary.tone === 'working'}
                data-testid="helper-sync-pill-drain"
              >
                {syncingLocal || summary.tone === 'working' ? t('NLSHelperSyncPillSyncing') : t('NLSHelperSyncPillSyncNow')}
              </Button>
              <Button
                variant="tertiary"
                size="md"
                className="w-full"
                onClick={() => {
                  onOpenSyncCenter()
                  setOpen(false)
                }}
                data-testid="helper-sync-pill-open-center"
                trailingIcon={<ArrowForwardRoundedIcon fontSize="small" aria-hidden="true" />}
              >
                {t('NLSHelperSyncPillOpenCenter')}
              </Button>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
